import type * as DismissableLayer from '@radix-ui/react-dismissable-layer';

/**
 * Radix's outside-interaction event, as handed to `onInteractOutside` on any
 * dismissable layer (Dialog, Sheet, AlertDialog, Popover).
 */
type InteractOutsideEvent = Parameters<
  NonNullable<
    React.ComponentPropsWithoutRef<typeof DismissableLayer.DismissableLayer>['onInteractOutside']
  >
>[0];

/**
 * Is this outside-interaction actually a click on a toast?
 *
 * Sonner renders its toaster in a body-level portal, so it sits outside every
 * modal's DOM and Radix counts a click there as an outside interaction — which
 * dismisses the modal. That is wrong for a toast raised BY the modal: the export
 * drawer's progress toast carries Download and Retry actions, and clicking
 * Download closed the drawer out from under the user.
 *
 * Read `event.detail.originalEvent.target`, not `event.target`: these are
 * `CustomEvent`s, so `event.target` is the layer itself
 * (`@radix-ui/react-dismissable-layer` `index.d.ts:42-46`).
 *
 * Pairs with the `pointer-events-auto` on `<Toaster>` in `app/app.tsx`, which is
 * what makes toasts clickable over a modal at all. Without both, a toast over a
 * modal is either inert or modal-closing — see `docs/pointer-events-cleanup.md`.
 */
export function isToastInteraction(event: InteractOutsideEvent): boolean {
  const target = event.detail.originalEvent.target;
  return target instanceof Element && target.closest('[data-sonner-toaster]') !== null;
}
